import React, { useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { useRouter } from "expo-router";

import PasswordInput from "./Components/PasswordInput";

import { ApiError } from "@/schemas/api/error";
import { isVerificationCodeValid } from "@/schemas/forms/code";
import { isValidPassword } from "@/schemas/forms/password";
import AuthService from "@/services/internal/auth";
import { alertUnexpectedApiError } from "@/utils/alerts";

type VerificationPasswordResetFormProps = {
  emailAddress: string;
};

function RestPasswordForm({
  emailAddress,
}: VerificationPasswordResetFormProps): React.JSX.Element {
  const router = useRouter();
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const validateInputs = (): boolean => {
    const codeValidation = isVerificationCodeValid(verificationCode);
    if (!codeValidation.isValid) {
      Alert.alert("Invalid Verification Code", codeValidation.message, [
        { text: "OK" },
      ]);
      return false;
    }

    const passwordValidation = isValidPassword(newPassword);
    if (!passwordValidation.isValid) {
      Alert.alert("Invalid Password", passwordValidation.message, [
        { text: "OK" },
      ]);
      return false;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert(
        "Password Mismatch",
        "The passwords you entered do not match. Please try again.",
        [{ text: "OK" }],
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    if (validateInputs()) {
      try {
        const authService = new AuthService();
        await authService.confirmPasswordReset({
          email: emailAddress,
          code: verificationCode,
          password: newPassword,
        });
        Alert.alert(
          "Password Updated",
          "Your password has been updated successfully. Please login with your new password.",
          [
            {
              text: "OK",
              onPress: () => router.replace("/(authentication)/sign-in"),
            },
          ],
        );
      } catch (error) {
        const apiError = error as ApiError;
        alertUnexpectedApiError(apiError);
      }
    }
  };

  const handleResendCode = async (): Promise<void> => {
    try {
      const authService = new AuthService();
      authService.passwordResetRequest(emailAddress);
      Alert.alert(
        "Verification Code Sent",
        `A new verification code has been sent to ${emailAddress}.`,
        [{ text: "OK" }],
      );
    } catch (error) {
      const apiError = error as ApiError;
      alertUnexpectedApiError(apiError);
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.formContainer}
      >
        <View style={styles.card}>
          <Text style={styles.title}>Change Password</Text>
          <Text style={styles.label}>Verification Code</Text>
          <TextInput
            style={styles.input}
            value={verificationCode}
            onChangeText={(text) =>
              setVerificationCode(text.replace(/[^0-9]/g, ""))
            }
            placeholder="Enter 6-digit code"
            keyboardType="number-pad"
            maxLength={6}
          />

          <Text style={styles.label}>New Password</Text>
          <PasswordInput
            value={newPassword}
            onChangeText={setNewPassword}
            placeholder="Enter new password"
          />

          <Text style={styles.label}>Confirm Password</Text>
          <PasswordInput
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder="Confirm new password"
          />

          <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
            <Text style={styles.submitButtonText}>Reset Password</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.resendButton}
            onPress={handleResendCode}
          >
            <Text style={styles.resendButtonText}>Resend Code</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  formContainer: {
    width: "100%",
    maxWidth: 400,
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: 10,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 30,
    textAlign: "center",
    color: "#4A5568",
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
    color: "#4A5568",
  },
  input: {
    backgroundColor: "white",
    borderRadius: 5,
    padding: 10,
    marginBottom: 15,
    fontSize: 16,
  },
  submitButton: {
    backgroundColor: "#4299E1",
    borderRadius: 5,
    padding: 15,
    alignItems: "center",
    marginTop: 10,
  },
  submitButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "600",
  },
  resendButton: {
    marginTop: 15,
    alignItems: "center",
  },
  resendButtonText: {
    color: "#4299E1",
    fontSize: 14,
    fontWeight: "600",
  },
});

export default RestPasswordForm;
